
import React, { useCallback, useEffect, useRef } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { IconAiOutlineDownload } from "@/utils/icons";

const ChartStackedColumnBarTest = (props: any) => {

  const { item } = props;
  const chartData = item?.data;
  const in_form_k = item?.in_form_k || false;

  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);


  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);


  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 400 ? chartRef.current.offsetWidth : 400;

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": "",
      "width": chartWidth,
      "height": 400,
      "padding": 0,
      "autosize": {
        "type": "fit",
        "contains": "padding"
      },
      "config": {
        "title": {
          "font": "Arial",
          "fontSize": 12
        },
        "text": {
          "font": "Arial",
          "fontSize": 10
        },
        "axis": {
          "labelFont": "Arial",
          "labelFontSize": 10,
          "labelColor": "gray"
        }
      },
      "title": {
        "text": item?.title || "",
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black"
      },
      "data": [
        {
          "name": "table",
          "values": chartData,
          "transform": [
            {
              "type": "stack",
              "groupby": [
                "x"
              ],
              "sort": {
                "field": "c",
                "order": "descending"
              },
              "field": "y"
            },
            {
              "type": "formula",
              "as": "color_value",
              "expr": "datum.c === 0 ? datum.y0_color : datum.y1_color"
            }
          ]
        },
        {
          "name": "totals",
          "source": "table",
          "transform": [
            {
              "type": "aggregate",
              "groupby": ["x" ],
              "ops": [ "sum" ],
              "fields": [ "y" ],
              "as": [  "totalY" ]
            }
          ]
        }
      ],
      "scales": [
        {
          "name": "x",
          "type": "band",
          "range": "width",
          "domain": {
            "data": "table",
            "field": "x"
          },
          "padding": 0.25,
          "round": true,
        },
        {
          "name": "y",
          "type": "linear",
          "range": "height",
          "nice": true,
          "zero": true,
          "domain": {
            "data": "table",
            "field": "y1"
          }
        },
        {
          "name": "color",
          "type": "ordinal",
          "range": {
            "data": "table",
            "field": "color_value"
          },
          "domain": {
            "data": "table",
            "field": "c"
          }
        }
      ],
      "axes": [
        {
          "orient": "bottom",
          "scale": "x",
          "labelLimit": { "signal": "bandwidth('x')" },
          "labelFontSize": 9,
          "labelColor": "black",
          "labelMultiline": true,
          "labelAlign": "center",
          "labelBaseline": "top"
        },
        {
          "orient": "left",
          "scale": "y",
          "zindex": 1
        }
      ],
      "marks": [
        {
          "type": "rect",
          "from": {
            "data": "table"
          },
          "encode": {
            "enter": {
              "x": {
                "scale": "x",
                "field": "x"
              },
              "width": {
                "scale": "x",
                "band": 1,
                "offset": -1
              },
              "y": {
                "scale": "y",
                "field": "y0"
              },
              "y2": {
                "scale": "y",
                "field": "y1"
              },
              "fill": {
                "scale": "color",
                "field": "c"
              }
            },
            "update": {
              "fillOpacity": {
                "value": 1
              }
            },
            "hover": {
              "fillOpacity": {
                "value": 0.5
              }
            }
          }
        },
        {
          "type": "text",
          "from": {
            "data": "table"
          },
          "encode": {
            "enter": {
              "x": {  "scale": "x",  "field": "x", "band": 0.5 },
              "y": {
                "signal": "scale('y', datum.y0 + (datum.y / 2))"
              },
              "text": {
                "signal": "datum.label_text ? datum.label_text : datum.y"
              },
              "align": {
                "value": "center"
              },
              "baseline": {
                "value": "middle"
              },
              "fill": {
                "value": "white"
              },
              "fontSize": {
                "value": 10
              },
              "fontWeight": {
                "value": "bold"
              }
            },
            "update": {
              "fillOpacity": {
                "signal": "datum.y > 0 ? 1 : 0"
              }
            }
          }
        },
        {
          "type": "text",
          "from": {
            "data": "totals"
          },
          "encode": {
            "enter": {
              "x": {
                "scale": "x",
                "field": "x",
                "band": 0.5
              },
              "y": {
                "scale": "y",
                "field": "totalY",
                "offset": -5
              },
              "text": {
                "signal": in_form_k ? "datum.totalY >= 1000 ? floor(datum.totalY / 1000) + 'k' : datum.totalY" : "datum.totalY"
              },
              "align": {
                "value": "center"
              },
              "baseline": {
                "value": "bottom"
              },
              "fill": {
                "value": "black"
              },
              "fontSize": {
                "value": 10
              },
              "fontWeight": {
                "value": "bold"
              }
            }
          }
        }
      ]
    }
    
    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((stackedColumnBarView: any) => {
      viewRef.current = stackedColumnBarView.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item]);


  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);


  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={handleExport}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div id="chart-stacked-column-bar" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
    </div>
  );
}

export default ChartStackedColumnBarTest;